import { useEffect } from 'react';
import { Navigate, Link as RouterLink, useNavigate } from 'react-router-dom';
import { signInPath } from 'common/config/urls';
import { SIGN_IN_VIEW } from 'common/constants/auth';
import { REDIRECT_MS, SSO_ERROR_REDIRECTING_IN_5_SECONDS } from 'common/constants/sso';
import { Button } from 'components/ui';
import { useReadFragmentFromUrl } from 'hooks';
import {
  AuthErrorPanel,
  AuthFormHeader,
  AuthPageStack,
  AuthTitleText,
} from 'pages/Auth/components';
import { useAuthSession } from 'pages/Auth/hooks';
import { buildSignInUrl } from 'pages/Auth/utils';

const SSO_ERROR_HEADER_LEAD = 'Single Sign-On';
const SSO_ERROR_PANEL_TITLE = 'We could not complete your sign-in';
const SSO_ERROR_MAIN_ACTION_LABEL = 'Back to sign in';

const SsoError = () => {
  const navigate = useNavigate();
  const { fragmentRef } = useReadFragmentFromUrl();
  const { hasSession } = useAuthSession();

  // We clear the provider fragment before rendering and then give the user a short window to read the error
  // before returning them to the regular sign-in entry point.
  useEffect(() => {
    const timer = setTimeout(() => {
      navigate(signInPath, { replace: true });
    }, REDIRECT_MS);

    return () => clearTimeout(timer);
  }, [fragmentRef, navigate]);

  if (hasSession) {
    return <Navigate replace to={buildSignInUrl({ view: SIGN_IN_VIEW.WORKSPACE_SELECTION })} />;
  }

  return (
    <AuthPageStack>
      <AuthFormHeader title={<AuthTitleText lead={SSO_ERROR_HEADER_LEAD} />} />
      <AuthErrorPanel
        description={SSO_ERROR_REDIRECTING_IN_5_SECONDS}
        mainAction={(
          <Button<typeof RouterLink>
            component={RouterLink}
            replace
            to={signInPath}
            variant="outlined"
            visualVariant="primaryOutline"
          >
            {SSO_ERROR_MAIN_ACTION_LABEL}
          </Button>
        )}
        title={SSO_ERROR_PANEL_TITLE}
      />
    </AuthPageStack>
  );
};

export default SsoError;
